import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Navbar } from '../components/Navbar'
import { Footer } from '../components/Footer'
import { Button } from '../components/ui/Button'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card'
import {MapPin, Clock, Share2, Download, AlertCircle, CheckCircle, Calendar, Eye} from 'lucide-react'
import { lumi } from '../lib/lumi'
import toast from 'react-hot-toast'
import { useAuth } from '../hooks/useAuth'
import { generateShareCard } from '../utils/shareCardGenerator'
import { generateWhatsAppMessage } from '../utils/shareTemplates'

export function RequestDetail() {
  const { requestId } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const [request, setRequest] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [shareCardUrl, setShareCardUrl] = useState<string | null>(null)
  const [generatingCard, setGeneratingCard] = useState(false)
  const [userProfile, setUserProfile] = useState<any>(null)

  useEffect(() => {
    loadRequest()
    if (user) {
      loadUserProfile()
    }
  }, [requestId, user])

  const loadUserProfile = async () => {
    if (!user) return
    try {
      const { list } = await lumi.entities.profiles.list({
        filter: { userId: user.userId }
      })
      if (list.length > 0) {
        setUserProfile(list[0])
      }
    } catch (error) {
      console.error('Failed to load profile:', error)
    }
  }

  const loadRequest = async () => {
    if (!requestId) return
    setLoading(true)
    try {
      const data = await lumi.entities.requests.get(requestId)
      setRequest(data)
      
      // Increment view count
      await lumi.entities.requests.update(requestId, {
        viewCount: (data.viewCount || 0) + 1
      })

      // Load existing card or generate new one
      if (data.shareCardUrl) {
        setShareCardUrl(data.shareCardUrl)
      } else {
        await generateAndSaveCard(data)
      }
    } catch (error) {
      console.error('Error loading request:', error)
      toast.error('Erro ao carregar solicitação')
    } finally {
      setLoading(false)
    }
  }

  const generateAndSaveCard = async (requestData: any) => {
    setGeneratingCard(true)
    try {
      const cardUrl = await generateShareCard({
        patientName: requestData.patientName,
        bloodType: requestData.bloodType,
        hospital: requestData.hospital,
        city: requestData.city,
        state: requestData.state,
        urgency: requestData.urgency,
        unitsNeeded: requestData.unitsNeeded,
        patientPhoto: requestData.patientPhoto
      })
      
      setShareCardUrl(cardUrl)
      
      // Save card URL to database
      await lumi.entities.requests.update(requestData._id, {
        shareCardUrl: cardUrl
      })
    } catch (error) {
      console.error('Error generating card:', error)
    } finally {
      setGeneratingCard(false)
    }
  }

  const handleDownloadCard = () => {
    if (!shareCardUrl) return
    const link = document.createElement('a')
    link.href = shareCardUrl
    link.download = `doe-${request?.bloodType || 'doacao'}.png`
    link.click()
    toast.success('📥 Baixando cartão...')
  }

  const handleShare = () => {
    if (!request) return
    const message = generateWhatsAppMessage({
      bloodType: request.bloodType,
      patientName: request.patientName,
      hospital: request.hospital,
      city: request.city,
      state: request.state,
      urgency: request.urgency,
      shareToken: request._id
    })
    const url = `https://wa.me/?text=${encodeURIComponent(message)}`
    window.open(url, '_blank')
    
    // Increment share count
    lumi.entities.requests.update(request._id, {
      shareCount: (request.shareCount || 0) + 1
    })
  }

  const handleConfirmDonation = async () => {
    if (!user || !userProfile) {
      toast.error('Faça login e complete seu perfil primeiro')
      return
    }

    try {
      const newTotalDonations = (userProfile.totalDonations || 0) + 1
      const newPoints = (userProfile.points || 0) + 100

      await lumi.entities.profiles.update(userProfile._id, {
        totalDonations: newTotalDonations,
        points: newPoints,
        lastDonationDate: new Date().toISOString()
      })

      toast.success('🎉 Doação confirmada! +100 pontos', { duration: 4000 })
      navigate('/ranking')
    } catch (error) {
      console.error('Failed to confirm donation:', error)
      toast.error('Erro ao confirmar doação')
    }
  }

  const getUrgencyLabel = (urgency: string) => {
    const labels: Record<string, string> = {
      critical: 'Crítico',
      high: 'Urgente',
      medium: 'Normal'
    }
    return labels[urgency] || urgency
  }

  const getUrgencyColor = (urgency: string) => {
    const colors: Record<string, string> = {
      critical: 'bg-red-100 text-red-800 border-red-300',
      high: 'bg-orange-100 text-orange-800 border-orange-300',
      medium: 'bg-blue-100 text-blue-800 border-blue-300'
    }
    return colors[urgency] || 'bg-gray-100 text-gray-800 border-gray-300'
  }

  const getDaysRemaining = (expiresAt?: string) => {
    if (!expiresAt) return null
    const now = new Date()
    const expiry = new Date(expiresAt)
    const diffTime = expiry.getTime() - now.getTime()
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    return diffDays
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="container mx-auto px-4 py-20 text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-red-600 mx-auto"></div>
          <p className="text-gray-600 mt-4">Carregando solicitação...</p>
        </div>
      </div>
    )
  }

  if (!request) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="container mx-auto px-4 py-20 text-center">
          <AlertCircle className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Solicitação não encontrada</h2>
          <Button onClick={() => navigate('/solicitacoes')}>Ver Todas as Solicitações</Button>
        </div>
      </div>
    )
  }

  const daysRemaining = getDaysRemaining(request.expiresAt)
  const isExpired = daysRemaining !== null && daysRemaining <= 0

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Expiration Warning */}
        {daysRemaining !== null && daysRemaining <= 3 && !isExpired && (
          <div className="mb-6 bg-orange-50 border-2 border-orange-300 rounded-lg p-4">
            <div className="flex items-center space-x-3">
              <AlertCircle className="h-6 w-6 text-orange-600" />
              <div>
                <p className="font-bold text-orange-900">
                  ⏰ Esta solicitação expira em {daysRemaining} {daysRemaining === 1 ? 'dia' : 'dias'}
                </p>
                <p className="text-sm text-orange-700">Compartilhe urgentemente para alcançar mais doadores!</p>
              </div>
            </div>
          </div>
        )}

        {isExpired && (
          <div className="mb-6 bg-red-50 border-2 border-red-300 rounded-lg p-4">
            <div className="flex items-center space-x-3">
              <AlertCircle className="h-6 w-6 text-red-600" />
              <div>
                <p className="font-bold text-red-900">⚠️ Esta solicitação expirou</p>
                <p className="text-sm text-red-700">Entre em contato com o solicitante para verificar o status atual.</p>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Request Header */}
            <Card className="border-l-4 border-l-red-600">
              <CardHeader>
                <div className="flex justify-between items-start mb-4">
                  <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-red-100 text-red-700 font-bold text-3xl">
                    {request.bloodType}
                  </div>
                  <span className={`px-3 py-1 rounded-full text-sm font-bold uppercase border-2 ${getUrgencyColor(request.urgency)}`}>
                    {getUrgencyLabel(request.urgency)}
                  </span>
                </div>
                <CardTitle className="text-3xl mb-2">{request.patientName}</CardTitle>
                <div className="space-y-2">
                  <p className="text-lg text-gray-700 flex items-center">
                    <MapPin className="h-5 w-5 mr-2 text-red-600" />
                    {request.hospital}
                  </p>
                  <p className="text-gray-600">
                    {request.city}, {request.state}
                  </p>
                </div>
              </CardHeader>
              <CardContent>
                {request.description && (
                  <div className="mb-4">
                    <h3 className="font-bold text-gray-900 mb-2">📝 Detalhes</h3>
                    <p className="text-gray-700">{request.description}</p>
                  </div>
                )}
                
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-sm text-gray-600">Unidades Necessárias</p>
                    <p className="text-2xl font-bold text-red-600">{request.unitsNeeded}</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-sm text-gray-600 flex items-center">
                      <Clock className="h-4 w-4 mr-1" />
                      Publicado em
                    </p>
                    <p className="text-lg font-semibold text-gray-900">
                      {new Date(request.createdAt).toLocaleDateString('pt-BR')}
                    </p>
                  </div>
                </div>

                {daysRemaining !== null && !isExpired && (
                  <div className="bg-blue-50 p-3 rounded-lg mb-4">
                    <p className="text-sm text-blue-600 flex items-center">
                      <Calendar className="h-4 w-4 mr-1" />
                      Validade
                    </p>
                    <p className="text-lg font-semibold text-blue-900">
                      {daysRemaining} {daysRemaining === 1 ? 'dia restante' : 'dias restantes'}
                    </p>
                  </div>
                )}

                <div className="flex items-center justify-between text-sm text-gray-500 pt-3 border-t">
                  <span className="flex items-center">
                    <Eye className="h-4 w-4 mr-1" />
                    {request.viewCount || 0} visualizações
                  </span>
                  <span className="flex items-center">
                    <Share2 className="h-4 w-4 mr-1" />
                    {request.shareCount || 0} compartilhamentos
                  </span>
                </div>
              </CardContent>
            </Card>

            {/* Contact Info */}
            {(request.contactEmail || request.contactPhone) && (
              <Card>
                <CardHeader>
                  <CardTitle>📞 Informações de Contato</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {request.contactEmail && (
                    <p className="text-gray-700">
                      <span className="font-semibold">Email:</span> {request.contactEmail}
                    </p>
                  )}
                  {request.contactPhone && (
                    <p className="text-gray-700">
                      <span className="font-semibold">WhatsApp:</span> {request.contactPhone}
                    </p>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Visual Card */}
            <Card className="border-2 border-red-300">
              <CardHeader>
                <CardTitle className="text-lg">🎨 Cartão Visual</CardTitle>
              </CardHeader>
              <CardContent>
                {generatingCard ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-red-600 mx-auto mb-3"></div>
                    <p className="text-sm text-gray-600">Gerando...</p>
                  </div>
                ) : shareCardUrl ? (
                  <div className="space-y-3">
                    <img 
                      src={shareCardUrl} 
                      alt="Cartão de doação" 
                      className="w-full rounded-lg border-2 border-red-200 shadow-lg cursor-pointer hover:scale-105 transition-transform"
                      onClick={handleDownloadCard}
                    />
                    <Button
                      onClick={handleDownloadCard}
                      className="w-full bg-red-600 hover:bg-red-700"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Baixar Imagem
                    </Button>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">Cartão não disponível</p>
                )}
              </CardContent>
            </Card>

            {/* Action Buttons */}
            <Card>
              <CardContent className="pt-6 space-y-3">
                <Button
                  onClick={handleShare}
                  className="w-full bg-green-600 hover:bg-green-700"
                >
                  <Share2 className="h-4 w-4 mr-2" />
                  Compartilhar no WhatsApp
                </Button>
                
                {userProfile && userProfile.bloodType === request.bloodType && !isExpired && (
                  <Button
                    onClick={handleConfirmDonation}
                    className="w-full bg-blue-600 hover:bg-blue-700"
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Confirmo que Doei
                  </Button>
                )}

                <Button
                  onClick={() => navigate('/solicitacoes')}
                  variant="outline"
                  className="w-full"
                >
                  Ver Todas as Solicitações
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  )
}
